import { axiosSecuredInstance } from "configurations/axiosConfig";
import { AdjustmentKind } from "utils/data/enum";

// Mirrors Stewart.Shared.Entities.PolicyData.PaginatedPolicyHistory
// (PolicyHistory + PolicyBaseEntity + paging columns). PascalCase on the wire
// because Startup.cs sets JsonSerializerOptions.PropertyNamingPolicy = null.
export type PolicyHistoryDto = {
  // PolicyHistory-specific
  PolicyHistoryID: number;
  PolicyTransactionID: number | null;
  AdjustmentKind?: AdjustmentKind;
  AdjustmentOfPolicyHistoryID: number | null;
  Reason: string | null;
  PriorPolicyDate: string | null;
  PriorUnderwriterCode: string | null;
  ShipDate: string | null;
  AgentOweAmountStatus: string | null;
  AgentOweAmountMethod: string | null;
  NumberOfOtherCharges: number | null;
  OtherChargeSequence: number | null;
  TicketNumber: string | null;
  MicrofilmNumber2: number | null;
  MicrofilmNumber3: number | null;
  TransferCount: number | null;
  IsExtractedForUDB: string | null;

  // PolicyBaseEntity
  PolicyImportID: number | null;
  PolicyImportDetailID: number | null;
  StateCode: string | null;
  // Two-letter abbreviation for the State grid column. Not returned by the API
  // yet — cells render blank until the SP/entity add it.
  StateAbbr: string | null;
  CountyDisplayName: string | null;
  AgentID: string | null;
  FileNumber: string | null;
  ProductTypeAbbrTypeCode: string | null;
  ProductTypeAbbrTypeName: string | null;
  FormPrefix: string | null;
  PolicyNumber: string | null;
  PolicyFormName: string | null;
  SIPolicyNumber: string | null;
  OriginalPolicyNumber: string | null;
  OriginalPolicyDate: string | null;
  OriginalPolicyCoverageType: string | null;
  ReportingID: string | null;
  InLieuPolicyNumber: string | null;
  UnderwriterTypeCode: string | null;
  UnderwriterTypeName: string | null;
  PolicyYear: string | null;
  PolicyMonth: string | null;
  PolicyDay: string | null;
  PolicyMonthDay: string | null;
  EffectiveDate: string | null;
  IssueDate: string | null;
  VoidedDate: string | null;
  BatchNumber: string | null;
  ProcessYear: string | null;
  ProcessMonth: string | null;
  ProcessDate: string | null;
  AccountingPeriod: string | null;
  ReceivedDate: string | null;
  KeyedDate: string | null;
  RecordTypeCode: string | null;
  RecordTypeName: string | null;
  PropertyTypeCode: string | null;
  PropertyTypeName: string | null;
  EscrowCode: string | null;
  CountyCode: string | null;
  MunicipalityNum: string | null;
  PropertyCity: string | null;
  PropertyZipCode: string | null;
  PricingRateType: string | null;
  TransactionTypeCode: string | null;
  TransactionTypeName: string | null;
  TransCode: string | null;
  TransCode2: string | null;
  TransCode3: string | null;
  TransCode4: string | null;
  TransCode5: string | null;
  TransCode6: string | null;
  TransCode7: string | null;
  TransCode8: string | null;
  RateAmount: number | null;
  RateAmount2: number | null;
  RateAmount3: number | null;
  RateAmount4: number | null;
  RateAmount5: number | null;
  RateAmount6: number | null;
  RateAmount7: number | null;
  RateAmount8: number | null;
  LiabilityAmount: number | null;
  ReissueLiabilityAmount: number | null;
  AdditionalLiabilityAmount: number | null;
  SILiabilityAmount: number | null;
  CalculatedFee: number | null;
  ActualFee: number | null;
  CalculatedPremiumTax: number | null;
  ActualPremiumTax: number | null;
  CalculatedRemittance: number | null;
  ActualRemittance: number | null;
  OriginalRemitSplitPct: number | null;
  NewRemitSplitPct: number | null;
  CalculatedRiskRate: number | null;
  ActualRiskRate: number | null;
  DiscountAmt: number | null;
  MiscStateVolume1: number | null;
  MiscStateVolume2: number | null;
  IsOverride: boolean | null;
  OverrideAmount: number | null;
  IsSimultaneous: boolean | null;
  IsHOI: boolean | null;
  IsReIssue: boolean | null;
  IsHavePolicy: boolean | null;
  IsEdited: boolean;
  EditedByUserID: string | null;
  EditedByUser: string | null;
  LastEditedDate: string | null;
  ClosedByUserName: string | null;
  ClosedDate: string | null;
  OverrideNoteText: string | null;
  OverrideNoteBy: string | null;
  OverrideNoteDate: string | null;
  PreOverrideActualFee: number | null;
  PreOverrideActualPremiumTax: number | null;
  PreOverrideActualRiskRate: number | null;
  PreOverrideActualRemittance: number | null;
  PreOverrideCapturedDate: string | null;
  ImageNumber: string | null;
  CheckNumber: string | null;
  IsMaxLiability: string | null;
  PctAmountPolicy: number | null;
  TreatyReinsurerCode: string | null;
  // int? — non-zero = national (drives the client-side "isNational" flag).
  IsMAST: number | null;
  KeypunchID: string | null;
  ExternalRefID: string | null;
  OriginationSource: string | null;
  OriginationUser: string | null;
  ChangesetID: string | null;

  // Paging metadata (PaginatedPolicyHistory)
  RowsPerPage: number | null;
  CurrentRow: number | null;
  TotalRows: number | null;
  CurrentPage: number | null;
  TotalPages: number | null;

  // Whole-query aggregates (same value on every row)
  TotalCount: number | null;
  TotalProducts: number | null;
  TotalOpenErrors: number | null;
  TotalSimultaneous: number | null;
  TotalLiabilityAmount: number | null;
  TotalActualFee: number | null;
  TotalActualRetention: number | null;
  TotalActualRemittance: number | null;
  TotalStates: number | null;

  // Field-level edits made after the record closed (PolicyAuditLog rows typed
  // FIELD_EDITED), same PascalCase-on-the-wire convention as the rest of this DTO.
  EditedFields?: PolicyHistoryEditedField[] | null;
};

export type PolicyHistoryEditedField = {
  PolicyAuditLogID: number;
  PolicyHistoryID: number;
  PolicyTransactionID: number | null;
  PolicyNumber: string | null;
  ActingUserID: string | null;
  ActingUser: string | null;
  PolicyAuditLogTypeCode: string;
  PolicyAuditLogTypeName: string | null;
  SubType: string | null;
  EventID: string;
  EventDate: string;
  FieldName: string;
  OldValue: string | null;
  NewValue: string | null;
  PolicyErrorID: number | null;
  PolicyErrorTypeCode: string | null;
  PolicyErrorTypeName: string | null;
  Notes: string | null;
  PolicyActivityID: number | null;
  OriginationSource: string | null;
  OriginationUser: string | null;
  IdentityKey: number;
  Active: number;
  CreatedDate: string;
  ModifiedDate: string;
  CreatedBy: string;
  ModifiedBy: string;
};

// Open-ended criteria: arbitrary PolicyBaseCriteria field names flow through
// via the index signature while the known fields stay typed.
export type PolicyHistoryCriteriaDto = {
  UserTimeZone: string;
  RequestedPage?: number;
  ListCount?: number;
  SortColumn?: string;
} & Record<string, unknown>;

export const getPolicyHistoryList = async (
  criteria: PolicyHistoryCriteriaDto
): Promise<PolicyHistoryDto[]> => {
  try {
    const { data } = await axiosSecuredInstance.post<PolicyHistoryDto[]>(
      "/PolicyHistory/GetPolicyHistoryListByCriteria",
      criteria
    );
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("getPolicyHistoryList failed", error);
    return [];
  }
};

export type EditPolicyHistoryDto = {
  PolicyHistoryID: number;
  PropertyTypeCode: string | null;
  CountyCode: string | null;
  MunicipalityNum: string | null;
};

export const savePolicyHistory = async (editDto: EditPolicyHistoryDto) => {
  try {
    const { data } = await axiosSecuredInstance.post(
      "/PolicyHistory/SavePolicyHistory",
      editDto
    );
    return data;
  } catch (error) {
    console.error("savePolicyHistory failed", error);
    throw error;
  }
};

export type PolicyHistoryAdjustmentDto = {
  PolicyHistoryID: number;
  IsReversalOnly: boolean;
  NewLiabilityAmount?: number | null;
  NewPremiumAmount?: number | null;
  NewRetentionAmount?: number | null;
  NewSplitPct: number | null;
};

export type PolicyHistoryBulkAdjustmentDto = {
  AdjustmentReason: string;
  Adjustments: PolicyHistoryAdjustmentDto[];
};

export const bulkAdjustPolicyHistory = async (
  bulkAdjust: PolicyHistoryBulkAdjustmentDto
): Promise<boolean> => {
  try {
    const { data } = await axiosSecuredInstance.post<{ Success?: boolean }>(
      "/PolicyHistory/BulkAdjust",
      bulkAdjust
    );
    return data?.Success ?? true;
  } catch (error) {
    console.error("bulkAdjustPolicyHistory failed", error);
    return false;
  }
};
